import type {
    IHeadlessBiometricsClient,
    StytchAPIError,
} from '@stytch/vanilla-js';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import isFuture from 'date-fns/isFuture';
import { supportedAuthenticationTypesAsync } from 'expo-local-authentication';
import { isWeb } from 'smoothie';

import { StorageKeys } from '../common/constants';
import { isEmpty } from '../common/misc';
import type { IStorage } from '../common/types/Storage';
import {
    captureException,
    captureMessage,
    captureWarning,
} from '../logger/Logger';
import { type User, UserType } from '../user/types/User';
import { ROUTE_PATHS_EXCLUDED_FROM_STORED_REDIRECT } from './constants';
import type { DeviceTokenResponse } from './types/Auth';

interface RedirectPath {
    pathname: string;
    storageTimestamp: string;
}

/**
 * RN Stytch biometrics also exposes deleteDeviceRegistration; the shared
 * IHeadlessBiometricsClient type from @stytch/vanilla-js does not.
 */
type BiometricsClientWithDeviceCleanup = IHeadlessBiometricsClient & {
    deleteDeviceRegistration?: () => Promise<boolean>;
};

const NO_ACTIVE_BIOMETRIC_REGISTRATIONS = 'no_active_biometric_registrations';

/**
 * Local Keychain keys can exist while Stytch no longer has a matching
 * biometric registration. Detect that known mismatch so we can heal and
 * avoid treating it as an unexpected error.
 */
export const isNoActiveBiometricRegistrationsError = (
    error: unknown,
): boolean => {
    if (error == null || typeof error !== 'object') {
        return false;
    }

    const stytchError = error as Partial<StytchAPIError> & {
        message?: string;
    };

    if (stytchError.error_type === NO_ACTIVE_BIOMETRIC_REGISTRATIONS) {
        return true;
    }

    return (
        typeof stytchError.message === 'string' &&
        stytchError.message.includes(NO_ACTIVE_BIOMETRIC_REGISTRATIONS)
    );
};

/**
 * Clears stale local biometric keys and re-opens the post-login Face ID
 * setup prompt so the member can re-enroll after password + MFA.
 */
export const healStaleBiometricRegistration = async (
    biometrics: BiometricsClientWithDeviceCleanup,
    storage: IStorage,
): Promise<void> => {
    // Biometrics Keychain cleanup is native-only; web never enrolls this factor.
    if (isWeb) {
        return;
    }

    try {
        if (biometrics.deleteDeviceRegistration != null) {
            await biometrics.deleteDeviceRegistration();
        } else {
            await biometrics.removeRegistration();
        }
        storage.setBool(StorageKeys.HAS_SEEN_BIOMETRICS_PROMPT, false);
        captureMessage(
            'Cleared stale local biometric registration after no_active_biometric_registrations',
        );
    } catch (healError) {
        captureWarning(
            'Failed to clear stale biometric registration',
            healError instanceof Error
                ? healError
                : new Error(String(healError)),
        );
    }
};

export const getDeviceAuthenticationTypes = async () => {
    try {
        return await supportedAuthenticationTypesAsync();
    } catch (err) {
        captureException(err);
        return [];
    }
};

export const storeUserToken = (userToken?: string) => {
    if (!isWeb || localStorage == null || userToken == null) {
        return;
    }

    localStorage.setItem(StorageKeys.USER_TOKEN, userToken);
};

export const removeUserToken = () => {
    if (!isWeb || localStorage == null) {
        return;
    }

    localStorage.removeItem(StorageKeys.USER_TOKEN);
};

const getDeviceToken = () => {
    if (!isWeb || localStorage == null) {
        return null;
    }

    const deviceTokenString = localStorage.getItem(StorageKeys.DEVICE_TOKEN);

    if (deviceTokenString == null) {
        return null;
    }

    return JSON.parse(deviceTokenString) as DeviceTokenResponse;
};

export const storeDeviceToken = (deviceToken: DeviceTokenResponse) => {
    if (!isWeb || localStorage == null) {
        return;
    }

    localStorage.setItem(StorageKeys.DEVICE_TOKEN, JSON.stringify(deviceToken));
};

export const removeDeviceToken = () => {
    if (!isWeb || localStorage == null) {
        return;
    }

    localStorage.removeItem(StorageKeys.DEVICE_TOKEN);
};

// Determines if a user is authenticated after signup. Used for allowing skipping of MFA post signup.
export const isAuthenticatedPostSignup = (user?: User) => {
    if (user?.type == null) return false;

    const isPrimaryOrPartner = [
        UserType.PRIMARY_MEMBER,
        UserType.PARTNER_MEMBER,
    ].includes(user?.type);
    return isPrimaryOrPartner && isUserFirstSession();
};

export const isUserFirstSession = () => {
    return getDeviceToken()?.isFirstLogin ?? false;
};

export const isUserDeviceMfaVerified = (stytchUserId?: string): boolean => {
    if (stytchUserId == null) {
        return false;
    }

    const deviceToken = getDeviceToken();

    if (deviceToken == null) {
        return false;
    }

    const isDeviceActive = isFuture(new Date(deviceToken.expires));
    const deviceMatchesUsers = stytchUserId === deviceToken.stytchId;

    return isDeviceActive && deviceMatchesUsers;
};

export const getStoredRedirectPath = (
    storage: IStorage,
    staleThresholdMinutes: number = 1,
): string | undefined => {
    let redirectPath: RedirectPath | null = null;
    const raw = storage.getString(StorageKeys.REDIRECT_PATH);
    if (raw != null) {
        try {
            redirectPath = JSON.parse(raw) as RedirectPath;
        } catch (err) {
            captureWarning(
                'Invalid JSON found in StorageKeys.REDIRECT_PATH',
                err,
            );

            // Clear the invalid json value.
            storage.removeItem(StorageKeys.REDIRECT_PATH);
        }
    }

    if (redirectPath == null || isEmpty(redirectPath)) {
        return undefined;
    }

    const shouldIgnorePath = ROUTE_PATHS_EXCLUDED_FROM_STORED_REDIRECT.some(
        (regexp) => regexp.test(redirectPath.pathname),
    );

    const isStale =
        differenceInMinutes(
            new Date(),
            redirectPath.storageTimestamp
                ? new Date(redirectPath.storageTimestamp)
                : new Date(),
        ) > staleThresholdMinutes;

    if (shouldIgnorePath || isStale) {
        clearRedirectPath(storage);
        return undefined;
    }

    return redirectPath.pathname;
};

export const setStoredRedirectPath = (storage: IStorage, pathname: string) => {
    const shouldIgnoreCurrentPath =
        ROUTE_PATHS_EXCLUDED_FROM_STORED_REDIRECT.some((regexp) =>
            regexp.test(pathname),
        ) || pathname.endsWith('8081'); // prevent causing a loop when running on localhost

    const existingRedirectPath = getStoredRedirectPath(storage);
    const hasExistingRedirectPath = existingRedirectPath !== undefined;

    if (shouldIgnoreCurrentPath || hasExistingRedirectPath) {
        if (
            pathname !== '/' &&
            hasExistingRedirectPath &&
            pathname !== existingRedirectPath
        ) {
            captureMessage(
                `Unexpected attempt to write to stored redirect path because a redirect path already exists; current path: ${pathname}, existing redirect path: ${existingRedirectPath}`,
            );
        }
        return false;
    } else {
        captureMessage(`Setting stored redirect path. pathname: ${pathname}`);
        storage.setString(
            StorageKeys.REDIRECT_PATH,
            JSON.stringify({
                pathname,
                storageTimestamp: new Date(),
            }),
        );
        return true;
    }
};

export const clearRedirectPath = (storage: IStorage) => {
    storage.removeItem(StorageKeys.REDIRECT_PATH);
};
